import Api from './Api';

export const login = async (values) => {
  const result = await Api.post('/admins/login', values);
  return result.data;
};

export const logout = async () => {
  Api.post('/admins/logout');
};

export const getProfile = async () => {
  const result = await Api.post('/admins/get-profile');
  return result.data;
};

export const listApprovals = async () => {
  const result = await Api.post('/admins/approvals/list');
  return result.data;
};

let categoriesCache;

async function loadCategories() {
  if (!categoriesCache) {
    const result = await Api.post('/admins/approvals/get-categories');
    categoriesCache = result.data;
  }
}

const flatCategories = (categories, parentId = null) => categories
  .flatMap((category) => {
    if (category.subCategories.length === 0) {
      return {
        ...category,
        parentId,
      };
    }
    return [
      {
        ...category,
        parentId,
      },
      ...flatCategories(category.subCategories, category._id),
    ];
  });
export const listCategories = async () => {
  await loadCategories();
  return categoriesCache;
};
let categoriesIndexCache;
export const getCategoriesIndex = async () => {
  if (categoriesIndexCache) return categoriesIndexCache;
  await loadCategories();
  categoriesIndexCache = flatCategories(categoriesCache)
    .reduce((obj, c) => {
      // eslint-disable-next-line no-param-reassign
      obj[c._id] = c;
      return obj;
    }, {});
  return categoriesIndexCache;
};

export const rejectDomain = async (domainId, reason, reasonText) => {
  const result = await Api.post('/admins/approvals/reject', {
    domainId,
    reason,
    reasonText,
  });
  return result.data;
};

export const approveDomain = async (domainId, values) => {
  const result = await Api.post('/admins/approvals/approve', {
    domainId,
    ...values,
    isYC: !!values.isYC,
  });
  return result.data;
};

export const fetch = async ({
  app,
  model,
  method,
  filter,
  sort,
  projection,
  populate,
  skip = 0,
  limit = 10000,
}) => {
  const result = await Api.post('/admins/fetch', {
    app,
    model,
    method,
    filter,
    sort,
    projection,
    populate,
    skip,
    limit,
  });
  return result.data;
};

const fToDomainFilter = (f) => ({
  ALL: {},
  LIVE: {
    status: 'APPROVED',
    live: true,
  },
  PAUSED: {
    status: 'APPROVED',
    live: false,
  },
  REJECTED: { status: 'REJECTED' },
  TO_REVIEW: { status: 'TO_REVIEW' },
  ARCHIVED: { archivedAt: { $ne: null } },
})[f];
export const listDomains = async ({
  page,
  q,
  f,
}) => fetch({
  model: 'Domain',
  method: 'find',
  filter: {
    ...fToDomainFilter(f),
    ...(q ? {
      name: {
        $regex: q,
        $options: 'i',
      },
    } : {}),
  },
  populate: ['userIds'],
  sort: { _id: -1 },
  ...(page ? {
    skip: (page - 1) * 20,
    limit: 20,
  } : {}),
});

export const countDomains = async ({
  q,
  f,
}) => fetch({
  model: 'Domain',
  method: 'countDocuments',
  filter: {
    ...fToDomainFilter(f),
    ...(q ? {
      name: {
        $regex: q,
        $options: 'i',
      },
    } : {}),
  },
});

export const listUsers = async ({
  page,
  q,
}) => {
  const users = await fetch({
    model: 'User',
    method: 'find',
    filter: {
      ...(q ? {
        $or: [
          {
            email: {
              $regex: q,
              $options: 'i',
            },
          },
          {
            'linkedinProfile.name': {
              $regex: q,
              $options: 'i',
            },
          },
        ],
      } : {}),
    },
    sort: { _id: -1 },
    skip: (page - 1) * 20,
    limit: 20,
  });
  const domains = await fetch({
    model: 'Domain',
    method: 'find',
    filter: { userIds: { $in: users.map((u) => u._id) } },
  });
  const userIdToDomains = domains.reduce((obj, domain) => (
    domain.userIds.reduce((o, userId) => ({
      ...o,
      [userId]: [
        ...(obj[userId] || []),
        domain,
      ],
    }), obj)
  ), {});
  return users.map((u) => ({
    ...u,
    domains: userIdToDomains[u._id] || [],
  }));
};

export const countUsers = async ({
  q,
}) => fetch({
  model: 'User',
  method: 'countDocuments',
  filter: {
    ...(q ? {
      $or: [
        {
          email: {
            $regex: q,
            $options: 'i',
          },
        },
      ],
    } : {}),
  },
});

const fToRequestFilter = (f) => ({
  ALL: {},
  PENDING: { status: 'PENDING' },
  ACCEPTED: { status: 'ACCEPTED' },
  REJECTED: { status: 'REJECTED' },
  EXPIRED: { status: 'EXPIRED' },
  DONE: { status: 'DONE' },
})[f];
const getDomainsWithQ = async (q) => {
  if (!q) return null;
  return fetch({
    model: 'Domain',
    method: 'find',
    filter: {
      name: {
        $regex: q,
        $options: 'i',
      },
    },
  });
};
export const listRequests = async ({
  page,
  f,
  q,
}) => {
  const domains = await getDomainsWithQ(q);
  return fetch({
    model: 'BacklinkRequest',
    method: 'find',
    filter: {
      ...fToRequestFilter(f),
      ...(domains ? {
        $or: [
          { from: { $in: domains.map((d) => d._id) } },
          { to: { $in: domains.map((d) => d._id) } },
        ],
      } : {}),
    },
    populate: ['from', 'to'],
    sort: { _id: -1 },
    skip: (page - 1) * 20,
    limit: 20,
  });
};

export const countRequests = async ({
  f,
  q,
}) => {
  const domains = await getDomainsWithQ(q);
  return fetch({
    model: 'BacklinkRequest',
    method: 'countDocuments',
    filter: {
      ...fToRequestFilter(f),
      ...(domains ? {
        $or: [
          { from: { $in: domains.map((d) => d._id) } },
          { to: { $in: domains.map((d) => d._id) } },
        ],
      } : {}),
    },
  });
};

export const impersonateUser = async (userId) => {
  const result = await Api.post('/admins/impersonate', { userId });
  return result.data;
};

export const archiveUser = async (userId) => {
  const result = await Api.post('/admins/archive-user', { userId });
  return result.data;
};

export const deleteUser = async (userId) => {
  const result = await Api.post('/admins/delete-user', { userId });
  return result.data;
};

export const getKPIs = async () => {
  const result = await Api.post('/admins/kpis');
  return result.data;
};

export const getBanner = async () => {
  const result = await Api.get('/announcements/banner');
  return result.data;
};

export const updateBanner = async (values) => {
  await Api.post('/admins/announcements/banner', values);
};

export const destroyPoints = (points) => Api.post('/admins/destroy-points', { points });
export const transferPoints = (domainId, points) => Api.post('/admins/transfer-points', {
  domainId,
  points,
});
export const stealPoints = (domainId, points) => Api.post('/admins/steal-points', {
  domainId,
  points,
});

export const archiveDomain = (domainId) => Api.post('/admins/archive-domain', { domainId });
export const unarchiveDomain = (domainId) => Api.post('/admins/unarchive-domain', { domainId });
